import { getQueryParams } from '~/utils/urlHelpers';
import { AuthClient } from '.';
import { OpenIDConnectClient, getRedirectUri } from './oidc/OpenIDConnectClient';
import { SessionStoragePKCEVerifier } from './oidc/PKCEVerifier';

export interface OpenIDClientOptions {
  clientId: string;
  discoveryUri: string;
  redirectUri: string;
  defaultIdp?: string;
  allowRefreshTokensWithRotation?: boolean;
}

export class OpenIDClient implements AuthClient {
  private client: OpenIDConnectClient;

  private targetUrl = '';

  constructor(private opts: OpenIDClientOptions) {
    this.client = new OpenIDConnectClient({
      clientId: opts.clientId,
      discoveryDocumentUrl: opts.discoveryUri,
      scope: ['openid', 'email', 'profile'],
      verifier: new SessionStoragePKCEVerifier(),
      redirectUri: opts.redirectUri,
      defaultIdp: opts.defaultIdp,
      allowRefreshTokensWithRotation: opts.allowRefreshTokensWithRotation,
    });
  }

  async login(): Promise<boolean> {
    const { href } = window.location;

    const { isRedirectUri, returnTo } = getRedirectUri({
      location: href,
      redirectUri: this.opts.redirectUri,
    });
    if (isRedirectUri) {
      const {
        code,
        error,
        error_description: errorDesc,
      } = getQueryParams({ href, queryParamsToParse: ['code', 'error', 'error_description'] });

      this.targetUrl = returnTo ?? '/';

      // eslint-disable-next-line no-console
      console.log(`Received: code=${!!code}, error=${error}, errorDesc=${errorDesc}`);

      if (!code) {
        throw new Error('no authorization code');
      }
      await this.client.exchangeCodeForTokens(code);
      return true;
    }

    return false;
  }

  redirectToLogin = async (): Promise<void> => {
    const { pathname, search } = window.location;

    await this.client.authorize({
      returnTo: `${pathname}${search}`,
    });
  };

  isAuthenticated = async (): Promise<boolean> => {
    return Promise.resolve(this.client.isAuthenticated);
  };

  getTargetUrl = (): string => {
    return this.targetUrl;
  };

  logout = async (params: any): Promise<void> => {
    await this.client.logout(params);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getTokenSilently = async (...params: any[]) => {
    const token = this.client.getAccessToken();

    if (!token) {
      throw new Error('Missing token');
    }

    return token;
  };
}
